/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import l10n from '../../shared/strings';
import { matchesChannelId } from './helpers/channelHelpers';
import {
  findListingEntity,
  listListingVariantsForChannel,
} from './helpers/entityReadHelpers';
import { removeListingVariant, upsertListing } from './helpers/entityAdapter';

export const removeVariantFromDraft = createAsyncThunk(
  'products/removeVariantFromDraft',
  async ({
    masterProductId, channelId, variantId,
  }, {
    extra: {
      sdk, Logger, showErrorDialog, utils,
    },
  }) => {
    try {
      await sdk.products.deleteVariant(variantId);
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg') || 'Can not delete product variant';
      showErrorDialog(errorMessage);
      Logger.error(new Error('Error delete listing product variant'), { error }, [
        'DeleteListingProductVariantFailed',
        { errorMessage: errorMessage || 'NoMessage' },
      ]);
      throw error;
    }
  },
  {
    condition: async ({ masterProductId, channelId, variantId }, { getState, extra: { showDialogAsync } }) => {
      const productsState = getState().products;
      const variant = productsState.entities?.listingVariants?.[variantId]
        || listListingVariantsForChannel(productsState, masterProductId, channelId)
          .find((entry) => entry._id === variantId);
      const isLive = Boolean(variant?.live?.storeId);
      const strings = l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_LISTING_VARIANT;
      try {
        await showDialogAsync({
          title: strings.TITLE,
          text: isLive ? strings.TEXT_LIVE(variant?.title || variantId) : strings.TEXT(variant?.title || variantId),
          textBtnCancel: strings.BTN_CANCEL,
          textBtnOk: strings.BTN_OK,
          buttonOkColor: 'danger',
        }).promise;
        return true;
      } catch {
        return false;
      }
    },
  },
);

export const reducer = (builder) => {
  builder
    .addCase(removeVariantFromDraft.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(removeVariantFromDraft.fulfilled, (state, { meta }) => {
      const { masterProductId, channelId, variantId } = meta.arg;
      state.isBusy = false;

      const existingListing = findListingEntity(state, { masterProductId, channelId });
      if (!existingListing?._id) return;

      const allChannelVariants = listListingVariantsForChannel(state, masterProductId, channelId);
      const deletedVariant = allChannelVariants.find(({ _id }) => _id === variantId);
      const nextListing = { ...existingListing };

      if (deletedVariant?.options) {
        const withoutVariants = {};

        Object.keys(deletedVariant.options).forEach((optionName) => {
          const optionValue = deletedVariant.options[optionName];
          const variantWithOption = allChannelVariants.find(
            (listingVariant) => listingVariant._id !== variantId
              && listingVariant.options?.[optionName] === optionValue,
          );
          if (!variantWithOption) withoutVariants[optionName] = optionValue;
        });

        Object.keys(withoutVariants).forEach((optionName) => {
          const optionValue = withoutVariants[optionName];
          (nextListing.availableOptions || []).forEach((option) => {
            if (option.name === optionName) {
              option.values.forEach((value) => {
                if (value.name === optionValue) {
                  value.hasVariants = false;
                }
              });
            }
          });
        });
      }

      removeListingVariant(state, variantId);
      upsertListing(state, {
        ...nextListing,
        masterProductId,
      });
    })
    .addCase(removeVariantFromDraft.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot delete listing variant';
    });
};
